import React, { useMemo, useState } from 'react';
import {
  Button,
  DropdownMenuContent,
  EmptyContent,
  Flex,
  Text,
  TextField,
} from '@/components/ui';
import { IconFileImport, IconSearch, IconX } from '@tabler/icons-react';
import { buildFormTree, FormTreeNode } from '../utils/formTreeUtils';
import { getFormDisplayName } from '@/utils/formUtils';
import { FormSelectorTreeItem } from './FormSelectorTreeItem';
import type { SelectedFile, StudyForm } from '../types';
import { MappingEngine } from '../../mapping-engine/MappingEngine';
import { MappingEngineAdapters } from '../../mapping-engine/adapters';
import './FormSelector.css';

interface FormSelectorDropdownProps {
  availableForms: StudyForm[];
  file?: SelectedFile;
  selectedForm?: StudyForm | null;
  nestedPath?: string; // JSON string with nested path structure
  onFormSelect: (formId: string, nestedPath: string) => void;
  useName?: boolean;
}

export const FormSelectorDropdown: React.FC<FormSelectorDropdownProps> = ({
  availableForms,
  file,
  selectedForm,
  nestedPath,
  onFormSelect,
  useName = false,
}) => {
  const fileName =
    file?.source === 'uploaded' ? file.file!.name : file?.csvFile?.filename;
  const [searchQuery, setSearchQuery] = useState('');

  const formTree = useMemo(() => {
    return buildFormTree(availableForms);
  }, [availableForms]);

  // Helper function to filter matches - only substring matches (form name in file name)
  const filterSubstringMatches = (
    matches: Array<{ form: StudyForm; score: number }>,
    fileName: string
  ): Array<{ form: StudyForm; score: number }> => {
    const fileNameLower = fileName.toLowerCase();
    return matches.filter(match => {
      const formName = match.form.name.toLowerCase();
      return fileNameLower.includes(formName);
    });
  };

  // Helper function to find perfect matches (child form + parent both match)
  const findPerfectMatches = (
    matches: Array<{ form: StudyForm; score: number }>,
    allForms: StudyForm[]
  ): Set<number> => {
    const perfectIds = new Set<number>();
    const matchIds = new Set(matches.map(m => m.form.id));

    for (const match of matches) {
      const form = match.form;
      // Check if this form is a child form
      const parent = allForms.find(p =>
        p.json_schema?.subFormArray?.includes(form.name)
      );

      if (parent && matchIds.has(parent.id)) {
        // Child form matches AND parent also matches
        perfectIds.add(form.id);
      }
    }

    return perfectIds;
  };

  // Get form matches with levels (perfect/good) for the file
  const matchLevels = useMemo(() => {
    if (!file || !availableForms.length) {
      return {
        matchLevels: new Map<number, 'perfect' | 'good'>(),
        matchScores: new Map<number, number>(),
      };
    }

    try {
      const fileName =
        file.source === 'uploaded' ? file.file!.name : file.csvFile?.filename || '';
      const fileNameWithoutExt = fileName.replace(/\.(csv|txt|tsv)$/i, '').trim();

      const mappingEngineFile =
        MappingEngineAdapters.selectedFileToMappingEngineFile(file);
      const mappingEngineForms = availableForms.map(f =>
        MappingEngineAdapters.studyFormToMappingEngineForm(f)
      );

      const allMatches = MappingEngine.getAllFormMatches(
        mappingEngineFile,
        mappingEngineForms
      );

      if (allMatches.length === 0) {
        return {
          matchLevels: new Map<number, 'perfect' | 'good'>(),
          matchScores: new Map<number, number>(),
        };
      }

      // Convert mapping engine forms back to original forms for filtering
      // We need to map by ID since MappingEngine returns forms with same IDs
      const matchesWithOriginalForms = allMatches.map(match => {
        const originalForm = availableForms.find(f => f.id === match.form.id);
        return {
          form: originalForm || match.form,
          score: match.score,
        };
      });

      // Filter to only substring matches (form name in file name)
      const substringMatches = filterSubstringMatches(
        matchesWithOriginalForms,
        fileNameWithoutExt
      );

      if (substringMatches.length === 0) {
        return {
          matchLevels: new Map<number, 'perfect' | 'good'>(),
          matchScores: new Map<number, number>(),
        };
      }

      // Find perfect matches (child form + parent both match)
      const perfectIds = findPerfectMatches(substringMatches, availableForms);

      // Get good matches (top-5 substring matches that are not perfect)
      const goodMatches = substringMatches
        .filter(m => !perfectIds.has(m.form.id))
        .slice(0, 5)
        .map(m => m.form.id);

      // Create map of match levels
      const levels = new Map<number, 'perfect' | 'good'>();
      perfectIds.forEach(id => levels.set(id, 'perfect'));
      goodMatches.forEach(id => levels.set(id, 'good'));

      // Create map of scores for all matches
      const matchScores = new Map<number, number>();
      substringMatches.forEach(match => {
        if (levels.has(match.form.id)) {
          matchScores.set(match.form.id, match.score);
        }
      });

      return { matchLevels: levels, matchScores };
    } catch (error) {
      console.warn('Failed to get form matches:', error);
      return {
        matchLevels: new Map<number, 'perfect' | 'good'>(),
        matchScores: new Map<number, number>(),
      };
    }
  }, [file, availableForms]);

  // Filter forms based on search query
  const filteredForms = useMemo(() => {
    if (!searchQuery) return formTree;

    const query = searchQuery.toLowerCase();

    // Recursively filter the tree
    function filterTree(nodes: FormTreeNode[]): FormTreeNode[] {
      return nodes
        .map(node => {
          const displayName = useName
            ? node.name
            : getFormDisplayName(node);
          const matches =
            node.name.toLowerCase().includes(query) ||
            displayName.toLowerCase().includes(query);
          const filteredChildren = filterTree(node.children);

          // Include node if it matches or has matching children
          if (matches || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        })
        .filter((node): node is FormTreeNode => node !== null);
    }

    return filterTree(formTree);
  }, [formTree, searchQuery, useName]);

  const handleInsertFileName = () => {
    if (file && fileName) {
      const baseFileName = fileName.replace(/\.csv$/i, '');
      setSearchQuery(baseFileName);
    }
  };

  return (
    <DropdownMenuContent
      className="form-selector-dropdown-content"
      variant="soft"
    >
      {/* File Info in Dropdown */}
      {file && (
        <div className="form-selector-file-info-container-selected">
          <Text size="3xsmall" color="textPrimary" weight="medium">
            Mapping for:{' '}
            <Button
              color="secondary"
              highContrast
              variant="text"
              size="xsmall"
              onClick={handleInsertFileName}
              title="Insert filename"
              startIcon={<IconFileImport />}
              ml="4"
              style={{
                display: 'inline-flex',
                verticalAlign: 'middle',
              }}
            >
              {fileName}
            </Button>
          </Text>
        </div>
      )}

      {/* Search Field */}
      <div className="form-selector-search-container-selected">
        <Flex gap="1">
          <div className="form-selector-search-input-wrapper">
            <TextField
              placeholder="Search forms..."
              color="primary"
              size="medium"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              startSlot={<IconSearch height="16" width="16" />}
              endSlot={
                searchQuery.length > 0 && (
                  <Button
                    color="error"
                    size="small"
                    variant="text"
                    isIconOnly
                    onClick={() => setSearchQuery('')}
                  >
                    <IconX />
                  </Button>
                )
              }
            />
          </div>
        </Flex>
      </div>

      {/* Tree-style dropdown */}
      {filteredForms.map(form => (
        <FormSelectorTreeItem
          key={form.id}
          form={form}
          level={0}
          availableForms={availableForms}
          selectedForm={selectedForm}
          nestedPath={nestedPath}
          pathFromRoot={[]}
          onSelect={onFormSelect}
          useName={useName}
          matchLevels={matchLevels.matchLevels}
          matchScores={matchLevels.matchScores}
        />
      ))}

      {/* No results */}
      {searchQuery && filteredForms.length === 0 && (
        <EmptyContent
          icon={<IconSearch />}
          size="small"
          title=" No forms found"
        />
      )}
    </DropdownMenuContent>
  );
};

